/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/ContributorDetails/ContributorDetails.tsx.
 */

import React from "react";
import Button from "../../../../../components/common/Button";
import "../../../../../styles/formBase.css";
import "./ContributorDetails.css";
import { useContributorDetails } from "./hooks/useContributorDetails";
import ContributorDetailsFieldRenderer from "./components/ContributorDetailsFieldRenderer";

interface ContributorDetailsProps {
  submissionId?: string;
  onNextStage: () => void;
}

const ContributorDetails: React.FC<ContributorDetailsProps> = ({
  submissionId,
  onNextStage,
}) => {
  const {
    formData,
    values,
    loading,
    errors,
    handleChange,
    handleSubmit,
    getAllFields,
    isBackgroundOtherEnabled,
    hasSavedOnce,
    isDirty,
  } = useContributorDetails(submissionId);

  const handleSave = async () => {
    await handleSubmit();
  };

  const handleNext = async () => {
    const success = await handleSubmit();
    if (success) {
      onNextStage();
    }
  };

  if (loading) return <p className="loading-text">Loading form…</p>;
  if (!formData) return <p>No form data available</p>;

  const allFields = getAllFields();

  return (
    <div className="form-container contributor-form-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>

      <form className="form-content">
        {allFields.map((field) => {
          if (!field.isEnabled) return null;

          const disabled =
            field.type === "READONLY" ||
            field.isEditable === false ||
            ["name", "email_address", "Contributor_ID"].includes(field.name);

          return (
            <ContributorDetailsFieldRenderer
              key={field.id}
              field={field}
              value={values[field.name] || ""}
              onChange={handleChange}
              error={errors[field.name]}
              disabled={disabled}
              isBackgroundOtherEnabled={isBackgroundOtherEnabled()}
            />
          );
        })}

        <div className="button-row">
          <Button
            label="Save"
            onClick={handleSave}
            type="button"
            disabled={loading || !isDirty}
          />
          <Button
            label="Next"
            onClick={handleNext}
            type="button"
            disabled={loading}
          />
        </div>
      </form>
    </div>
  );
};

/** React component — Contributor Details. */
export default ContributorDetails;